import React, { useEffect, useMemo, useState } from "react";
import "../styles/table.css";

export interface Column {
  key: string;
  label: string;
  render?: (row: any, rowIndex: number, filteredData: any[]) => React.ReactNode;
  imageFolder?:
    | "users"
    | "subscribers"
    | "products"
    | "brands"
    | "subcategories"
    | "about"
    | "latestnews"
    | "career"
    | "team"
    | "software"
    | "software-modules";

  // Optional: set false if you do not want column filter on a specific column
  filterable?: boolean;
}

interface Props {
  columns?: Column[];
  data?: any[];
  loading?: string;
  selectedRowId?: number | null;
  onRowSelect?: (row: any) => void;

  onFilterChange?: (filteredData: any[], hasActiveFilters: boolean) => void;

  getRowKey?: (row: any, index: number) => React.Key;
  getRowClassName?: (row: any) => string;
  isRowExpanded?: (row: any) => boolean;
  renderExpandedRow?: (
    row: any,
    rowIndex: number,
    filteredData: any[],
  ) => React.ReactNode;
  expandedRowClassName?: string;
}

export default function DataTable({
  columns = [],
  data = [],
  selectedRowId,
  onRowSelect,
  onFilterChange,
  getRowKey,
  getRowClassName,
  isRowExpanded,
  renderExpandedRow,
  expandedRowClassName,
}: Props) {
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!target.closest(".admin-column-filter-wrap")) {
        setOpenFilterKey(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getColumnValue = (row: any, key: string) => {
    const value = row?.[key];

    if (value === null || value === undefined || value === "") {
      return "";
    }

    return String(value).toLowerCase();
  };

  const safeData = useMemo<any[]>(() => {
    if (Array.isArray(data)) {
      return data;
    }

    console.error("DataTable received non-array data:", data);

    return [];
  }, [data]);

  const filteredData = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(
      ([, value]) => value.trim() !== "",
    );

    if (activeFilters.length === 0) return safeData;

    return safeData.filter((row) =>
      activeFilters.every(([key, value]) =>
        getColumnValue(row, key).includes(value.trim().toLowerCase()),
      ),
    );
  }, [safeData, columnFilters]);

  const hasActiveColumnFilters = useMemo(() => {
    return Object.values(columnFilters).some(
      (value) => String(value || "").trim() !== "",
    );
  }, [columnFilters]);

  useEffect(() => {
    onFilterChange?.(filteredData, hasActiveColumnFilters);
  }, [filteredData, hasActiveColumnFilters, onFilterChange]);

  const hasColumnFilter = (key: string) =>
    Boolean(columnFilters[key] && columnFilters[key].trim() !== "");

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearColumnFilter = (key: string) => {
    setColumnFilters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  return (
    <div className="admin-table-wrapper">
      <table className="admin-table">
        <thead>
          <tr>
            {columns.map((col) => {
              const isFilterable = col.filterable !== false && !col.imageFolder;

              return (
                <th key={col.key}>
                  <div className="admin-th-content">
                    <span className="admin-column-label">
                      {col.label}

                      {hasColumnFilter(col.key) ? (
                        <strong className="admin-column-filter-count">
                          ({filteredData.length})
                        </strong>
                      ) : null}
                    </span>

                    {isFilterable && (
                      <div
                        className="admin-column-filter-wrap"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          className={`admin-column-filter-btn ${
                            hasColumnFilter(col.key) ? "active" : ""
                          }`}
                          onClick={() => {
                            setOpenFilterKey((current) =>
                              current === col.key ? null : col.key,
                            );
                          }}
                          title={`Filter ${col.label}`}
                          aria-label={`Filter ${col.label}`}
                        >
                          ▾
                        </button>

                        {openFilterKey === col.key && (
                          <div
                            className="admin-column-filter-popover"
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="text"
                              autoFocus
                              placeholder={`Filter ${col.label}...`}
                              value={columnFilters[col.key] || ""}
                              onChange={(event) =>
                                handleColumnFilterChange(
                                  col.key,
                                  event.target.value,
                                )
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Escape") {
                                  setOpenFilterKey(null);
                                }

                                if (event.key === "Enter") {
                                  setOpenFilterKey(null);
                                }
                              }}
                            />

                            {hasColumnFilter(col.key) && (
                              <button
                                type="button"
                                className="admin-column-filter-clear"
                                onClick={() => {
                                  clearColumnFilter(col.key);
                                }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {filteredData.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length || 1}
                style={{ textAlign: "center", padding: "20px" }}
              >
                No data available
              </td>
            </tr>
          ) : (
            filteredData.map((row, index) => {
              const rowKey = getRowKey
                ? getRowKey(row, index)
                : (row?.id ?? index);

              const isSelectedRow =
                selectedRowId !== undefined &&
                selectedRowId !== null &&
                row?.id !== undefined &&
                row?.id !== null &&
                selectedRowId === row.id;

              const rowClassName = [
                isSelectedRow ? "row-selected" : "",
                getRowClassName ? getRowClassName(row) : "",
              ]
                .filter(Boolean)
                .join(" ");

              const expanded = renderExpandedRow && isRowExpanded?.(row);

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className={rowClassName}
                    onClick={() => {
                      if (onRowSelect) {
                        onRowSelect(row);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (!onRowSelect) return;

                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowSelect(row);
                      }
                    }}
                    tabIndex={onRowSelect ? 0 : undefined}
                    role={onRowSelect ? "button" : undefined}
                    style={{
                      cursor: onRowSelect ? "pointer" : "default",
                    }}
                  >
                    {columns.map((col) => (
                      <td key={col.key}>
                        {col.render ? (
                          col.render(row, index, filteredData)
                        ) : col.imageFolder && row?.[col.key] ? (
                          <img
                            src={`${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/${
                              col.imageFolder === "products"
                                ? `products/${row.id}`
                                : col.imageFolder
                            }/${row[col.key]}`}
                            alt="Preview"
                            style={{
                              width: "50px",
                              height: "50px",
                              objectFit: "contain",
                              borderRadius: "8px",
                            }}
                          />
                        ) : (
                          (row?.[col.key] ?? "-")
                        )}
                      </td>
                    ))}
                  </tr>

                  {expanded && (
                    <tr
                      className={
                        expandedRowClassName || "admin-table-expanded-row"
                      }
                    >
                      <td colSpan={columns.length}>
                        {renderExpandedRow(row, index, filteredData)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
